import { useEffect, useRef, type ReactNode } from "react";
import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";

// A section's data as the page holds it. Nothing renders in place of data
// that has not arrived: loading says so, and an error offers a retry.
export type Load<T> = { status: "idle" } | { status: "loading" } | { status: "error" } | { status: "ready"; data: T };

export const thClass = "px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-chrome-dim";
export const tdClass = "px-3 py-3 align-top text-[13px] text-chrome";
export const inputClass =
  "h-10 w-full rounded-md border border-ground-edge bg-ground px-3 text-[13px] text-chrome placeholder:text-chrome-dim focus:border-gold/60 focus:outline-none";
export const primaryButton =
  "bf-focusable inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-money px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:opacity-60";
export const ghostButton =
  "bf-focusable inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-ground-edge bg-ground px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-chrome-dim transition-colors hover:border-gold/50 hover:text-gold disabled:cursor-not-allowed disabled:opacity-60";
export const dangerButton =
  "bf-focusable inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-alert/50 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-alert transition-colors hover:bg-alert hover:text-ground disabled:cursor-not-allowed disabled:opacity-60";
export const smallButton =
  "bf-focusable inline-flex items-center justify-center whitespace-nowrap rounded-sm border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors disabled:cursor-not-allowed disabled:opacity-60";

export function Chip({ tone, children }: { tone: "money" | "gold" | "dim" | "alert"; children: ReactNode }) {
  const tones = {
    money: "bg-money/15 text-money",
    gold: "bg-gold/15 text-gold",
    dim: "bg-chrome-dim/15 text-chrome-dim",
    alert: "bg-alert/15 text-alert",
  };
  return (
    <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${tones[tone]}`}>{children}</span>
  );
}

export function Loading() {
  return <p className="py-16 text-center text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{copy.admin.loading}</p>;
}

export function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="py-14 text-center">
      <p className="text-sm text-chrome-dim">{copy.admin.loadFailed}</p>
      <button type="button" onClick={onRetry} className="bf-focusable mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-money">
        {copy.admin.retry}
      </button>
    </div>
  );
}

export function Empty({ icon, title, sub }: { icon: IconName; title: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-ground-edge text-2xl text-chrome-dim">
        <Icon name={icon} />
      </span>
      <p className="mt-4 text-sm font-bold uppercase tracking-[0.14em] text-chrome">{title}</p>
      {sub && <p className="mt-2 max-w-xs text-[13px] text-chrome-dim">{sub}</p>}
    </div>
  );
}

// ConfirmDialog asks before a money or destructive action. Nothing is sent
// unless Confirm is pressed.
export function ConfirmDialog({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ground/80 px-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-confirm-text"
        className="w-full max-w-md rounded-2xl border border-ground-edge bg-ground-raised p-6"
      >
        <p id="admin-confirm-text" className="text-sm leading-relaxed text-chrome">
          {message}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button ref={cancelRef} type="button" onClick={onCancel} className={ghostButton}>
            {copy.admin.cancel}
          </button>
          <button type="button" onClick={onConfirm} className={primaryButton}>
            {copy.admin.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
